import $ from 'cash-dom';
import { get, reduce, includes, isEmpty, forEach } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { CAMPAIGN_NAMES } from 'constants/personalizations';
import Cart from 'containers/CartContainer';
import * as actions from 'state/actions';
import { ADD_TO_CART_ANALYTICS_ORIGINS } from 'state/constants';
import CartSelector from 'state/selectors/cart';
import I18n from 'utils/I18n';
import Log from 'utils/Log';
import Settings from 'utils/Settings';
import { parseQueryString, removeUrlParam } from 'utils/Url';

const CartController = class {
  static get props() {
    return {};
  }

  static get urlParams() {
    return parseQueryString();
  }

  static get cartProducts() {
    return get(CartController.urlParams, 'cartProducts');
  }

  static get cartDiscount() {
    return get(CartController.urlParams, 'discount');
  }

  static get addToCartId() {
    return get(CartController.urlParams, 'add_on');
  }

  static get isOnCartPage() {
    return $('.page-wrapper').hasClass('cart-page');
  }

  static get isCartDrawerOpen() {
    return get(CartController.urlParams, 'openCartDrawer');
  }

  static mountCartContainer = store => {
    /* istanbul ignore next */
    ReactDOM.render(
      <Provider store={store}>
        <Cart {...CartController.props} />
      </Provider>,
      document.getElementById('cart-container')
    );
  };

  static bindCloseCartOnEscape = store => {
    document.addEventListener(
      'keydown',
      e => {
        if (e.keyCode === 27) {
          store.dispatch(actions.closeCart());
        }
      },
      false
    );
  };

  static bindShoprunnerEvent = store => {
    window.addEventListener(
      'ShopRunnerChanged',
      () => store.dispatch(actions.handleShoprunnerCart()),
      false
    );
  };

  static reconstructCart = store => {
    const state = store.getState();
    const currentCart = CartSelector.getCart(state);
    const currentCartProducts = currentCart.itemIds;

    try {
      const cartProductsJSON = JSON.parse(CartController.cartProducts);
      const cartProductsToAdd = reduce(
        cartProductsJSON,
        (itemsToAdd, item) => {
          const storefrontProductId = window.atob(get(item, 'variantId'));
          const productId = storefrontProductId.split('gid://shopify/ProductVariant/')[1];
          const modifiedItems = { ...itemsToAdd };
          if (!includes(currentCartProducts, productId)) {
            modifiedItems[productId] = item.quantity;
          }
          return modifiedItems;
        },
        {}
      );

      if (!isEmpty(cartProductsToAdd)) {
        store.dispatch(actions.addLineItems(cartProductsToAdd), {
          origination: ADD_TO_CART_ANALYTICS_ORIGINS.CART,
        });
      } else {
        store.dispatch(actions.openCart());
      }

      const numCartItemsToAdd = Object.keys(cartProductsToAdd).length;
      store.dispatch(actions.trackCartReconstructionEvent(numCartItemsToAdd));
    } catch (error) {
      Log.warn('Could not reconstruct cart.', error);
    }

    removeUrlParam('cartProducts');
  };

  static checkPriceChange = store => {
    const state = store.getState();
    const currentCartItems = CartSelector.getCartLineItems(state);

    const localCartJSON = window?.localStorage?.getItem('cart') || null;
    store.dispatch(actions.setCartCache());

    if (!localCartJSON) return;

    try {
      const localCart = JSON.parse(localCartJSON);

      let hasPriceMismatch = false;
      forEach(currentCartItems, item => {
        const cachedPrice = localCart[item.id];
        if (cachedPrice && cachedPrice !== item.price) {
          hasPriceMismatch = true;
        }
      });

      if (hasPriceMismatch) {
        store.dispatch(actions.setCartMessage(I18n.localize('cart.priceChange')));
      }
    } catch (error) {
      Log.warn('Error parsing cart cache', error);
    }
  };

  static openCartFromQueryParam = store => {
    store.dispatch(actions.openCart('#query-param'));
    removeUrlParam('openCartDrawer');
  };

  // object of {[variantId]: quantity}
  static addExternalProductsToCart = store => productsToAdd => {
    store.dispatch(actions.addLineItems(productsToAdd), {
      origination: ADD_TO_CART_ANALYTICS_ORIGINS.CART,
    });
  };

  static mountExposedAddToCart = store => {
    window.ALLBIRDS = window.ALLBIRDS || {};
    window.ALLBIRDS.addItemsToCart = CartController.addExternalProductsToCart(store);
  };

  static fetchDyCampaigns = store => {
    store.dispatch(
      actions.fetchPersonalizations({
        names: [CAMPAIGN_NAMES.RETURNS_INSURANCE_UPSELL],
      })
    );
  };

  /**
   * Dispatching in a non-standard way here due to some strange behavior where thunk isn't returning
   * the promise correctly from the dispatch method.
   */
  static initCart = async store => actions.initCart()(store.dispatch);

  static _addDiscounts = async store =>
    actions.addDiscounts([CartController.cartDiscount])(store.dispatch);

  static init = async store => {
    if (CartController.isCartDrawerOpen) {
      CartController.openCartFromQueryParam(store);
    }

    // Add any add-on item from URL param
    if (CartController.addToCartId) {
      store.dispatch(
        actions.addLineItems(
          { [CartController.addToCartId]: 1 },
          { origination: ADD_TO_CART_ANALYTICS_ORIGINS.CART }
        )
      );
    }

    CartController.fetchDyCampaigns(store);
    CartController.bindCloseCartOnEscape(store);
    CartController.bindShoprunnerEvent(store);
    CartController.mountExposedAddToCart(store);
    CartController.mountCartContainer(store);

    await CartController.initCart(store);
    CartController.checkPriceChange(store);

    if (CartController.cartProducts) {
      CartController.reconstructCart(store);
    }

    // Add discount is not supported on AJAX API
    // Instead use built in feature by Shopify, ie: https://allbirds.com/discount/DISCOUNT_CODE
    if (CartController.cartDiscount && Settings.storefrontCartEnabled) {
      await CartController._addDiscounts(store);
      removeUrlParam('discount');
    }

    if (Settings.shoprunnerEnabled) {
      store.dispatch(actions.handleShoprunnerCart());
    }
  };
};

export default CartController;
